// Packages
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { cloneDeep } from "lodash";

// MUI
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Divider,
  Typography,
  Grid,
  Chip
} from "@mui/material";

// Components
import GlobalAlert from "components/Notification";
import { SolidButton } from "components/buttons/SolidButton";
import PillTab from 'components/PillTab';
import { Loading } from 'components/Loading';
import { OutlineButton } from 'components/buttons/OutlineButton';
import CheckboxField from "components/CheckboxField";
import LinkModal from "components/LinkModal";

// Custom UI
import { StyledRadioButtonGroup } from "./components/StyledRadioButtonGroup";
import { StyledMenuSelect } from './components/StyledMenuSelect';
import { StyledTimeChipSelector } from './components/StyledTimeChipSelector';
import { StyledInput } from "./components/StyledInput";
import { StyledLabel } from "./components/StyledLabel";
import { StyledBox } from "./components/StyledBox";
import { StyledChannelBox } from "./components/StyledChannelBox";
import { ChannelTooltips } from './components/ChannelTooltips';

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useZustandStore from "zustandStore";

// Helper
import {
  getMainSendRequestParams,
  getInitRadioData,
  getInitNeighborAPScheduleList,
  getInitAutoPowerScheduleList,
  getCleanPayload,
  getIsFormInvalid,
  getInitChannels,
  getInitChannelWidths,
  getInitWirelessModes,
  getInitTxPowers,
} from "./helper";

// Const
import {
  defaultAlertInfo,
  defaultBandTabs,
  defaultWirelessModes,
  defaultChannelWidths,
  defaultTxPowers,
  defaultUtilizationList,
  defaultRSSIThresholdList,
} from "./const";

const Radio = ({
  showEditGeneral,
  selectCountry
}) => {
  const [uuids] = useState(() => ({
    read: uuidv4(),
    update: uuidv4(),
  }));
  const theme = useTheme();
  const navigate = useNavigate();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();
  const isUnityProSubscribed = useZustandStore((state) => state.isUnityProSubscribed);

  const [alertInfo, setAlertInfo] = useState(structuredClone(defaultAlertInfo));
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  // Global radio.
  const [radioData, setRadioData] = useState(null);
  const [radioOriginalData, setRadioOriginalData] = useState(null);
  const neighborAPScheduleList = useMemo(() => getInitNeighborAPScheduleList({ radioData }), [radioData]);
  const autoPowerScheduleList = useMemo(() => getInitAutoPowerScheduleList({ radioData }), [radioData]);

  // Sub Radio
  const [bandTabs, setBandTabs] = useState(structuredClone(defaultBandTabs));
  const selectedBand = useMemo(() => bandTabs.find(tab => tab.isSelected), [bandTabs]);
  const selectedRadioDataIndex = useMemo(() => {
    return radioData?.["wireless.radio"]?.findIndex(radio => radio.band === selectedBand.value) ?? -1;
  }, [radioData?.["wireless.radio"], selectedBand.value]);

  const [channels, setChannels] = useState({});

  const [chanWidths, setChanWidths] = useState(cloneDeep(defaultChannelWidths));
  const [wirelessModes, setWirelessModes] = useState(cloneDeep(defaultWirelessModes));
  const [txPowers, setTxPowers] = useState(cloneDeep(defaultTxPowers));
  const [utilizationList, setUtilizationList] = useState(cloneDeep(defaultUtilizationList));
  const [rssiThresholdList, setRssiThresholdList] = useState(cloneDeep(defaultRSSIThresholdList));

  const onUpdateGlobalRadioData = ({
    updateData = {}
  }) => {

    setRadioData(prev => {
      return {
        ...prev,
        "wireless.radioGlobal": {
          ...prev["wireless.radioGlobal"],
          ...updateData
        }
      }
    })

  }

  const onUpdateSubRadioData = ({
    updateData = {}
  }) => {
    setRadioData(prev => {
      const updatedRadioList = prev["wireless.radio"].map((radio, index) => {
        return index === selectedRadioDataIndex ? { ...radio, ...updateData } : radio;
      });

      return {
        ...prev,
        "wireless.radio": updatedRadioList,
      };
    });
  };

  const checkHasActiveProSettings = (data) => {
    if (!data) return false;
    const global = data["wireless.radioGlobal"] || {};
    if (global.neighborApDetection === true) return true;
    if (global.smartWifi === true) return true;
    if (global.autoChannelAdjustment === true || global.autoPowerAdjustment === true) return true;

    const radioList = data["wireless.radio"] || [];
    for (const radio of radioList) {
      if (radio.multicastBandwidthControl === true) return true;
      if (radio.connectionLimit === true) return true;
      if (radio.rssiThresholdEnable === true) return true;
    }
    return false;
  };

  const onSave = () => {
    if (!isUnityProSubscribed && checkHasActiveProSettings(radioData)) {
      setIsLinkModalOpen(true);
      return;
    }

    setIsLoading(true);

    const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "update" });
    const payload = getCleanPayload({ radioData });

    sendRequest(
      apiUrl,
      uuids.update,
      requestParams,
      payload
    );
  }

  const onCancel = () => {
    setRadioData(structuredClone(radioOriginalData));
  }

  const fetchRadioData = () => {
    setIsLoading(true);

    if (isAuthReady) {
      const { apiUrl, requestParams } = getMainSendRequestParams({ auth, method: "read" });

      sendRequest(
        apiUrl,
        uuids.read,
        requestParams
      );
    }
  }

  useEffect(() => {
    fetchRadioData();
  }, [isAuthReady]);

  useEffect(() => {
    if (!dataMap?.[uuids.read]) return;

    const response = getData(uuids.read);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the Radio data.",
        });
      } else {

        const initRadioData = getInitRadioData({
          responseData
        });

        setRadioData(initRadioData);
        setRadioOriginalData(initRadioData);
        setBandTabs(prev => {
          const existBands = new Set(initRadioData?.["wireless.radio"]?.map(radio => radio.band) ?? []);
          const defaultSelectedBand = ["2", "5", "6"].find(band => existBands.has(band));

          return prev.map(bandTab => {
            return {
              ...bandTab,
              isSelected: defaultSelectedBand ? bandTab.value === defaultSelectedBand : bandTab.isSelected
            }
          })
        });
      }

      setIsLoading(false);
    }
  }, [dataMap?.[uuids.read]]);

  useEffect(() => {
    if (!dataMap?.[uuids.update]) return;

    const response = getData(uuids.update);
    const responseData = response?.data ?? response ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to update the Radio data.",
        });

        fetchRadioData();

      }

      setIsLoading(false);
    }
  }, [dataMap?.[uuids.update]]);

  useEffect(() => {

    if (!radioData?.["wireless.radio"]?.[selectedRadioDataIndex]) return;

    // Channel
    const initChannels = getInitChannels({
      channel: radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.autoChannels,
      selectedBand,
      selectCountry
    });

    setChannels(cloneDeep(initChannels));

    // ChanWidth
    const initChanWidths = getInitChannelWidths({
      channelWidth: radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.chanWidth,
      selectedBand
    });

    setChanWidths(cloneDeep(initChanWidths));

    // Wireless Mode
    const initWirelessModes = getInitWirelessModes({
      wirelessMode: radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.wirelessMode,
      selectedBand
    });

    setWirelessModes(cloneDeep(initWirelessModes));

    // Tx Power
    const initTxPowers = getInitTxPowers({
      txPower: radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.txPower,
      selectedBand
    });

    setTxPowers(cloneDeep(initTxPowers));

  }, [selectedRadioDataIndex, selectedBand, selectCountry, radioData]);

  return (
    <>
      {isLoading && <Loading />}

      {/* Global Settings */}
      <Box sx={{ padding: "10px 20px" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[1],
          }}
        >
          Global Settings
        </Typography>

        <Divider sx={{ margin: "20px 0" }} />

        {/* Airtime Fairness */}
        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Airtime Fairness",
            value: !!radioData?.["wireless.radioGlobal"]?.airtimeFairness,
            onChange: value => {
              onUpdateGlobalRadioData({
                updateData: {
                  "airtimeFairness": value
                }
              })
            }
          }}
        />

        {/* Band Steering */}
        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Band Steering",
            value: !!radioData?.["wireless.radioGlobal"]?.bandSteering,
            onChange: value => {
              onUpdateGlobalRadioData({
                updateData: {
                  "bandSteering": value
                }
              })
            }
          }}
        />

        {/* Neighbor AP Detection */}
        <Box sx={{ display: "flex" }}>
          <StyledRadioButtonGroup
            {...{
              showEditGeneral,
              label: "Neighbor AP Detection",
              isPro: true,
              value: !!radioData?.["wireless.radioGlobal"]?.neighborApDetection,
              onChange: value => {
                onUpdateGlobalRadioData({
                  updateData: {
                    "neighborApDetection": value
                  }
                })
              }
            }}
          />
        </Box>

        {/* Start Time (Each day) */}
        {
          !!radioData?.["wireless.radioGlobal"]?.neighborApDetection &&
          <StyledTimeChipSelector
            label="Start Time (Each day)"
            timeList={neighborAPScheduleList}
            max={9}
            onAddTimeChip={time => {
              onUpdateGlobalRadioData({
                updateData: {
                  "neighborApDetectionSchedule": [...radioData["wireless.radioGlobal"].neighborApDetectionSchedule, time]
                }
              });
            }}
            onDeleteTimeChip={time => {
              onUpdateGlobalRadioData({
                updateData: {
                  "neighborApDetectionSchedule": radioData["wireless.radioGlobal"].neighborApDetectionSchedule.filter(loopTime => loopTime != time)
                }
              });
            }}
          />
        }

        {/* Smart Wi-Fi Disconnect */}
        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Smart Wi-Fi Disconnect",
            isPro: true,
            value: !!radioData?.["wireless.radioGlobal"]?.smartWifi,
            tooltip: "When enabled, the AP will automatically disconnect all Wi-Fi clients if the Ethernet connection is lost, preventing devices from staying on an offline network.",
            onChange: value => {
              onUpdateGlobalRadioData({
                updateData: {
                  "smartWifi": value
                }
              })
            }
          }}
        />

      </Box>

      {/* WLAN Optimization */}
      <Box sx={{ padding: "20px 20px 10px" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[1],
          }}
        >
          WLAN Optimization
        </Typography>

        <Divider sx={{ margin: "20px 0" }} />

        {/* Auto Channel Adjustment */}
        <StyledRadioButtonGroup
          {...{
            showEditGeneral,
            label: "Auto Channel Adjustment",
            isPro: true,
            value: !!radioData?.["wireless.radioGlobal"]?.autoChannelAdjustment,
            onChange: value => {
              onUpdateGlobalRadioData({
                updateData: {
                  "autoChannelAdjustment": value
                }
              })
            }
          }}
        />

        {/* - Auto Power Adjustment */}
        {
          !!radioData?.["wireless.radioGlobal"]?.autoChannelAdjustment &&
          <>
            <StyledRadioButtonGroup
              {...{
                showEditGeneral,
                label: "- Auto Power Adjustment",
                value: !!radioData?.["wireless.radioGlobal"]?.autoPowerAdjustment,
                isIndent: true,
                onChange: value => {
                  onUpdateGlobalRadioData({
                    updateData: {
                      "autoPowerAdjustment": value
                    }
                  })
                }
              }}
            />

            {/* - Start Time (Each day) */}
            <StyledTimeChipSelector
              label="- Start Time (Each day)"
              timeList={autoPowerScheduleList}
              max={12}
              isIndent
              onAddTimeChip={time => {
                onUpdateGlobalRadioData({
                  updateData: {
                    "autoPowerSchedule": [...radioData["wireless.radioGlobal"].autoPowerSchedule, time]
                  }
                });
              }}
              onDeleteTimeChip={time => {
                onUpdateGlobalRadioData({
                  updateData: {
                    "autoPowerSchedule": radioData["wireless.radioGlobal"].autoPowerSchedule.filter(loopTime => loopTime != time)
                  }
                });
              }}
            />
          </>
        }


      </Box>

      <Divider sx={{ margin: "20px 0" }} />

      {/* Sub Radio Content */}
      <Box
        sx={{
          ml: 2,
          mb: "50px"
        }}
      >
        <Box sx={{
          my: 4
        }}>

          {/* Band selection */}
          <PillTab
            options={bandTabs}
            value={selectedBand.value}
            onChange={band => {
              setBandTabs(prev => {
                return prev.map(loopBand => {
                  return {
                    ...loopBand,
                    isSelected: loopBand.value === band.value
                  }
                })
              })
            }}
          />
        </Box>

        {
          radioData?.["wireless.radio"]?.[selectedRadioDataIndex] &&
          <Box>
            {/* Eligible Auto Channels */}
            <StyledLabel label="Eligible Auto Channels">
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", width: "60%" }}>
                {
                  (channels?.[selectedBand.value] || []).map(item => (
                    <StyledChannelBox
                      key={selectedBand.value + "-" + item?.id}
                      value={item?.value}
                      isSelected={item?.isSelected}
                      onClick={ch => {
                        const clonedChannels = cloneDeep(channels);

                        clonedChannels[selectedBand?.value] = channels[selectedBand?.value].map(loopCh => {
                          if (loopCh.value === ch) return { ...loopCh, isSelected: !loopCh.isSelected }
                          else return loopCh;
                        });

                        setChannels(cloneDeep(clonedChannels));

                        const flatChannels = clonedChannels[selectedBand.value]
                          .filter(ch => ch.isSelected)
                          .map(ch => ch.value)

                        onUpdateSubRadioData({ updateData: { autoChannels: flatChannels } });
                      }}
                    />
                  ))
                }
              </Box>
            </StyledLabel>

            <StyledLabel>
              <ChannelTooltips />
            </StyledLabel>

            {/* Channel Width */}
            <StyledMenuSelect
              width="325px"
              showEditGeneral={showEditGeneral}
              options={selectedBand.value ? chanWidths[selectedBand.value] : []}
              label="Channel Width"
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.chanWidth}
              onChange={chw => {
                setChanWidths(prev => {
                  return {
                    ...prev,
                    [selectedBand.value]: prev?.[selectedBand.value].map(chanWidth => {
                      return {
                        ...chanWidth,
                        isSelected: chanWidth.value === chw.value
                      }
                    })
                  };
                });

                const updateData = { "chanWidth": chw.value };
                if (selectedBand.value === "2" && String(chw.value) === "20") {
                  updateData["h2040Coexistence"] = false;
                }

                onUpdateSubRadioData({ updateData });
              }}
            />

            {/* HT20/40 Coexistence" */}
            {
              selectedBand?.value === "2" &&
              <Box
                sx={{
                  mt: 1,
                  mb: 2,
                  ml: "352px",
                }}
              >
                <CheckboxField
                  label="HT20/40 Coexistence"
                  checked={
                    String(radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.chanWidth ?? "") === "20"
                      ? false
                      : !!radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.h2040Coexistence
                  }
                  disabled={
                    !showEditGeneral ||
                    String(radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.chanWidth ?? "") === "20"
                  }
                  onChange={value => onUpdateSubRadioData({ updateData: { "h2040Coexistence": value } })}
                />
              </Box>
            }

            {/* Wireless Mode */}
            <StyledMenuSelect
              width="325px"
              showEditGeneral={showEditGeneral}
              options={selectedBand.value ? wirelessModes[selectedBand.value] : []}
              label="Wireless Mode"
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.wirelessMode}
              onChange={mode => {
                setWirelessModes(prev => {
                  return {
                    ...prev,
                    [selectedBand.value]: prev?.[selectedBand.value].map(wirelessMode => {
                      return {
                        ...wirelessMode,
                        isSelected: wirelessMode.value === mode.value
                      }
                    })
                  };
                });

                onUpdateSubRadioData({ updateData: { "wirelessMode": mode.value } });
              }}
            />

            {/* Maximum Output Power */}
            <StyledMenuSelect
              width="325px"
              showEditGeneral={showEditGeneral}
              options={selectedBand.value ? txPowers[selectedBand.value] : []}
              label="Maximum Output Power"
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.txPower}
              onChange={power => {
                setTxPowers(prev => {
                  return {
                    ...prev,
                    [selectedBand.value]: prev?.[selectedBand.value].map(txPower => {
                      return {
                        ...txPower,
                        isSelected: txPower.value === power.value
                      }
                    })
                  };
                });

                onUpdateSubRadioData({ updateData: { "txPower": power.value } });
              }}
            />

            {/* DTIM Interval */}
            <StyledLabel
              label="DTIM Interval"
            >
              <StyledInput
                type="number"
                disabled={!showEditGeneral}
                value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.dtimInterval ?? ""}
                isInvalid={
                  radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.dtimInterval < 1 ||
                  radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.dtimInterval > 15
                }
                onChange={e => onUpdateSubRadioData({ updateData: { "dtimInterval": e.target.value === "" ? "" : +e.target.value } })}
              />
              <Typography
                sx={{
                  color: theme.palette.primary.greySecound,
                  fontSize: theme.typography.customFontSize[1],
                  ml: 1
                }}
              >
                (1~15)
              </Typography>
            </StyledLabel>

            {/* Short GI */}
            <StyledRadioButtonGroup
              showEditGeneral={showEditGeneral}
              label="Short GI"
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.shortGi}
              onChange={value => onUpdateSubRadioData({ updateData: { "shortGi": value } })}
            />

            {/* IGMP Snooping */}
            <StyledRadioButtonGroup
              showEditGeneral={showEditGeneral}
              label="IGMP Snooping"
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.igmpSnooping}
              onChange={value => onUpdateSubRadioData({ updateData: { "igmpSnooping": value } })}
            />

            {/* Multicast Bandwidth Control */}
            <StyledRadioButtonGroup
              showEditGeneral={showEditGeneral}
              label="Multicast Bandwidth Control"
              isPro={true}
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.multicastBandwidthControl}
              onChange={value => onUpdateSubRadioData({ updateData: { "multicastBandwidthControl": value } })}
            />

            {/* - Maximum Multicast Bandwidth */}
            {
              radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.multicastBandwidthControl &&
              <StyledLabel
                label="- Maximum Multicast Bandwidth"
                isIndent={true}
              >
                <StyledInput
                  type="number"
                  disabled={!showEditGeneral}
                  value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.maxMulticastBandwidth ?? ""}
                  isInvalid={
                    radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.maxMulticastBandwidth < 1 ||
                    radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.maxMulticastBandwidth > 1024
                  }
                  onChange={e => onUpdateSubRadioData({ updateData: { "maxMulticastBandwidth": e.target.value === "" ? "" : +e.target.value } })}
                />
                <Typography
                  sx={{
                    color: theme.palette.primary.greySecound,
                    fontSize: theme.typography.customFontSize[1],
                    ml: 1
                  }}
                >
                  (1~1024)
                </Typography>
              </StyledLabel>
            }

            {/* Change DHCPOFFER from Broadcast to Unicast */}
            <StyledRadioButtonGroup
              showEditGeneral={showEditGeneral}
              label="Change DHCPOFFER from Broadcast to Unicast"
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.broadcastToUnicast}
              onChange={value => onUpdateSubRadioData({ updateData: { "broadcastToUnicast": value } })}
            />

            {/* RTS Length */}
            <StyledLabel
              label="RTS Length"
            >
              <StyledInput
                type="number"
                disabled={!showEditGeneral}
                value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.rtsLength ?? ""}
                isInvalid={
                  radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.rtsLength < 256 ||
                  radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.rtsLength > 2346
                }
                onChange={e => onUpdateSubRadioData({ updateData: { "rtsLength": e.target.value === "" ? "" : +e.target.value } })}
              />
              <Typography
                sx={{
                  color: theme.palette.primary.greySecound,
                  fontSize: theme.typography.customFontSize[1],
                  ml: 1
                }}
              >
                (256-2346)
              </Typography>
            </StyledLabel>

            {/* Fragment Length */}
            <StyledLabel
              label="Fragment Length"
            >
              <StyledInput
                type="number"
                disabled={!showEditGeneral}
                value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.fragmentLength ?? ""}
                isInvalid={
                  radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.fragmentLength < 256 ||
                  radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.fragmentLength > 2346
                }
                onChange={e => onUpdateSubRadioData({ updateData: { "fragmentLength": e.target.value === "" ? "" : +e.target.value } })}
              />
              <Typography
                sx={{
                  color: theme.palette.primary.greySecound,
                  fontSize: theme.typography.customFontSize[1],
                  ml: 1
                }}
              >
                (256-2346)
              </Typography>
            </StyledLabel>

            {/* Connection Limit */}
            <StyledRadioButtonGroup
              showEditGeneral={showEditGeneral}
              label="Connection Limit"
              isPro={true}
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.connectionLimit}
              onChange={value => onUpdateSubRadioData({ updateData: { "connectionLimit": value } })}
            />

            {
              radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.connectionLimit &&
              <>
                {/* - User Limit */}
                <StyledLabel
                  label="- User Limit"
                  isIndent={true}
                >
                  <StyledInput
                    type="number"
                    disabled={!showEditGeneral}
                    value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.userLimit ?? ""}
                    isInvalid={
                      radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.userLimit < 0 ||
                      radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.userLimit > 64
                    }
                    onChange={e => onUpdateSubRadioData({ updateData: { "userLimit": e.target.value === "" ? "" : +e.target.value } })}
                  />
                  <Typography
                    sx={{
                      color: theme.palette.primary.greySecound,
                      fontSize: theme.typography.customFontSize[1],
                      ml: 1
                    }}
                  >
                    (0~64)
                  </Typography>
                </StyledLabel>

                {/* - Network Utilization */}
                <StyledMenuSelect
                  width="325px"
                  isIndent={true}
                  showEditGeneral={showEditGeneral}
                  options={utilizationList}
                  label="- Network Utilization"
                  value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.networkUtilization}
                  onChange={util => {
                    setUtilizationList(prev => {
                      return prev.map(utilization => {
                        return {
                          ...utilization,
                          isSelected: utilization.value === util.value
                        }
                      })
                    });

                    onUpdateSubRadioData({ updateData: { "networkUtilization": util.value } });
                  }}
                />
              </>
            }

            {/* Minimum RSSI */}
            <StyledRadioButtonGroup
              showEditGeneral={showEditGeneral}
              label="Minimum RSSI"
              isPro={true}
              value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.rssiThresholdEnable}
              onChange={value => onUpdateSubRadioData({ updateData: { "rssiThresholdEnable": value } })}
            />

            {/* - Minimum RSSI Threshold */}
            {
              radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.rssiThresholdEnable &&
              <StyledMenuSelect
                width="325px"
                isIndent={true}
                showEditGeneral={showEditGeneral}
                options={rssiThresholdList}
                label="- Minimum RSSI Threshold"
                value={radioData?.["wireless.radio"]?.[selectedRadioDataIndex]?.rssiThreshold}
                onChange={rssi => {
                  setRssiThresholdList(prev => {
                    return prev.map(rssiThreshold => {
                      return {
                        ...rssiThreshold,
                        isSelected: rssiThreshold.value === rssi.value
                      }
                    })
                  });

                  onUpdateSubRadioData({ updateData: { "rssiThreshold": rssi.value } });
                }}
              />
            }

          </Box>
        }

      </Box>

      {
        showEditGeneral && (
          <Box>
            <Grid
              container
              style={{ justifyContent: "center", width: "100%" }}
            >
              <Box style={{ margin: "10px" }}>
                <OutlineButton
                  width="135px"
                  height="42px"
                  onClick={() => onCancel()}
                  disabled={showEditGeneral ? false : true}
                >
                  Cancel
                </OutlineButton>
              </Box>

              <Box style={{ margin: "10px" }}>
                <SolidButton
                  width="135px"
                  height="42px"
                  onClick={() => onSave()}
                  disabled={getIsFormInvalid({ radioData })}
                >
                  Save
                </SolidButton>
              </Box>
            </Grid>
          </Box>
        )
      }

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo(structuredClone(defaultAlertInfo))}
      />

      <LinkModal
        open={isLinkModalOpen}
        transparentBackdrop
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? "/msp/license" : "/license");
        }}
      />
    </>
  );
};

export default Radio;
